// Guest Pickup Report
// Per-guest pickup details (expected/actual time, VID, plate, color, pickup notes)
// over a selectable timeframe (week, month, quarter, semi-annual, annual).
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessReports } from '../utils/roles';
import { formatFullDateTime } from '../utils/dateUtils';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/GuestActivityReports.css';
import '../styles/GuestPickupReport.css';

const TIMEFRAME_LABELS = {
  week: 'Past Week',
  month: 'Past Month',
  quarter: 'Past Quarter (3 Months)',
  'semi-annual': 'Past 6 Months',
  annual: 'Past Year (12 Months)',
};

const getTimeframeRange = (timeframe) => {
  const now = new Date();
  const start = new Date();
  switch (timeframe) {
    case 'week': start.setDate(now.getDate() - 7); break;
    case 'month': start.setMonth(now.getMonth() - 1); break;
    case 'quarter': start.setMonth(now.getMonth() - 3); break;
    case 'semi-annual': start.setMonth(now.getMonth() - 6); break;
    case 'annual': start.setFullYear(now.getFullYear() - 1); break;
    default: start.setDate(now.getDate() - 7);
  }
  return { start, end: now };
};

const formatTime12h = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const mm = parts[1];
  if (isNaN(h)) return timeStr;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${period}`;
};

const visitHasPickupData = (v) =>
  Boolean(v.expectedPickupTime || v.actualPickupTime || v.vidNumber || v.licPlateNumber || v.vehicleColor || v.pickupNotes);

const GuestPickupReport = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('week');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    getUserRole(user.uid).then((role) => {
      if (!canAccessReports(role)) {
        navigate('/dashboard');
      } else {
        setAuthChecked(true);
      }
    });
  }, [user, navigate]);

  useEffect(() => {
    if (authChecked) {
      generateReport(timeframe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, timeframe]);

  const generateReport = async (tf) => {
    setLoading(true);
    setError('');
    try {
      const { start, end } = getTimeframeRange(tf);
      const checkInsRef = collection(db, 'checkIns');
      const q = query(
        checkInsRef,
        where('checkInTime', '>=', Timestamp.fromDate(start)),
        where('checkInTime', '<=', Timestamp.fromDate(end))
      );

      const [snap, participantsSnap] = await Promise.all([
        getDocs(q),
        getDocs(collection(db, 'participants')),
      ]);

      const participantsMap = new Map();
      participantsSnap.forEach((d) => {
        const data = d.data();
        if (data.firstName && data.lastName) {
          participantsMap.set(`${data.firstName}_${data.lastName}`, data);
        }
      });

      const guestMap = new Map();
      let totalVisits = 0;
      let visitsWithPickupData = 0;

      snap.forEach((doc) => {
        const c = { id: doc.id, ...doc.data() };
        if (c.guestDeleted === true) return;
        if (c.serviceType === 'note') return;

        const visit = {
          id: c.id,
          checkInTime: c.checkInTime,
          checkOutTime: c.checkOutTime,
          status: c.status,
          expectedPickupTime: c.expectedPickupTime || '',
          actualPickupTime: c.actualPickupTime || '',
          vidNumber: c.vidNumber || '',
          licPlateNumber: c.licPlateNumber || '',
          vehicleColor: c.vehicleColor || '',
          pickupNotes: c.pickupNotes || '',
        };

        totalVisits += 1;
        if (!visitHasPickupData(visit)) return;
        visitsWithPickupData += 1;

        const key = `${c.guestFirstName} ${c.guestLastName}`;
        if (!guestMap.has(key)) {
          const participantInfo = participantsMap.get(`${c.guestFirstName}_${c.guestLastName}`);
          guestMap.set(key, {
            name: key,
            firstName: c.guestFirstName,
            lastName: c.guestLastName,
            image: participantInfo?.image || null,
            visits: [],
          });
        }
        guestMap.get(key).visits.push(visit);
      });

      const guests = Array.from(guestMap.values())
        .map((g) => ({
          ...g,
          visits: g.visits.sort((a, b) => {
            const ta = a.checkInTime?.toMillis ? a.checkInTime.toMillis() : 0;
            const tb = b.checkInTime?.toMillis ? b.checkInTime.toMillis() : 0;
            return tb - ta;
          }),
        }))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setReportData({
        timeframe: tf,
        startDate: start,
        endDate: end,
        guests,
        totalVisits,
        visitsWithPickupData,
        totalGuests: guests.length,
      });
    } catch (err) {
      console.error('Error generating Guest Pickup Report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredGuests = !reportData
    ? []
    : reportData.guests.filter((g) =>
        searchTerm.trim()
          ? g.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
          : true
      );

  const handleSelectSearchGuest = (guest) => {
    setSearchTerm(guest.name);
    setShowSearchDropdown(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSearchDropdown(false);
  };

  const exportToCSV = () => {
    if (!reportData) return;
    const headers = [
      'Guest Name',
      'Check-In Date',
      'Check-Out Date',
      'Expected Pickup Time',
      'Actual Pickup Time',
      'VID #',
      'LIC Plate #',
      'Vehicle Color',
      'Pickup Notes',
    ];
    const rows = [];
    filteredGuests.forEach((guest) => {
      guest.visits.forEach((v) => {
        rows.push([
          guest.name,
          v.checkInTime ? formatFullDateTime(v.checkInTime.toDate ? v.checkInTime.toDate() : v.checkInTime) : '',
          v.checkOutTime ? formatFullDateTime(v.checkOutTime.toDate ? v.checkOutTime.toDate() : v.checkOutTime) : '',
          formatTime12h(v.expectedPickupTime),
          formatTime12h(v.actualPickupTime),
          v.vidNumber,
          v.licPlateNumber,
          v.vehicleColor,
          v.pickupNotes,
        ]);
      });
    });
    const escapeCsv = (val) => {
      const s = String(val == null ? '' : val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const periodLabel = TIMEFRAME_LABELS[reportData.timeframe] || reportData.timeframe;
    const periodRange = `${reportData.startDate.toLocaleDateString()} - ${reportData.endDate.toLocaleDateString()}`;
    const titleRows = [
      ['Guest Pickup Report'],
      ['Period', periodLabel, periodRange],
      ['Generated', new Date().toLocaleString()],
      [],
    ];
    const csv = [...titleRows, headers, ...rows]
      .map((r) => r.map(escapeCsv).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    a.download = `guest-pickup-report-${reportData.timeframe}-${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  if (!authChecked) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  const renderVisitTable = (guest) => (
    <div className="pickup-table-wrapper">
      <table className="pickup-table">
        <colgroup>
          <col className="col-checkin" />
          <col className="col-expected" />
          <col className="col-actual" />
          <col className="col-vid" />
          <col className="col-plate" />
          <col className="col-color" />
          <col className="col-notes" />
        </colgroup>
        <thead>
          <tr>
            <th>Check-In Date</th>
            <th>Expected Pickup</th>
            <th>Actual Pickup</th>
            <th>VID #</th>
            <th>LIC Plate #</th>
            <th>Vehicle Color</th>
            <th>Pickup Notes</th>
          </tr>
        </thead>
        <tbody>
          {guest.visits.map((v) => (
            <tr key={v.id}>
              <td>{v.checkInTime ? formatFullDateTime(v.checkInTime.toDate ? v.checkInTime.toDate() : v.checkInTime) : '—'}</td>
              <td>{formatTime12h(v.expectedPickupTime) || '—'}</td>
              <td>{formatTime12h(v.actualPickupTime) || '—'}</td>
              <td>{v.vidNumber || '—'}</td>
              <td>{v.licPlateNumber || '—'}</td>
              <td>{v.vehicleColor || '—'}</td>
              <td className="pickup-notes-cell">{v.pickupNotes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderVisitCards = (guest) => (
    <div className="visit-cards">
      {guest.visits.map((v) => (
        <div key={v.id} className="visit-card">
          <div className="visit-card-header">
            {v.checkInTime ? formatFullDateTime(v.checkInTime.toDate ? v.checkInTime.toDate() : v.checkInTime) : '—'}
          </div>
          <div className="visit-card-grid">
            <div className="visit-card-cell">
              <div className="visit-card-label">Expected Pickup</div>
              <div className="visit-card-value">{formatTime12h(v.expectedPickupTime) || '—'}</div>
            </div>
            <div className="visit-card-cell">
              <div className="visit-card-label">Actual Pickup</div>
              <div className="visit-card-value">{formatTime12h(v.actualPickupTime) || '—'}</div>
            </div>
            <div className="visit-card-cell">
              <div className="visit-card-label">VID #</div>
              <div className="visit-card-value">{v.vidNumber || '—'}</div>
            </div>
            <div className="visit-card-cell">
              <div className="visit-card-label">LIC Plate #</div>
              <div className="visit-card-value">{v.licPlateNumber || '—'}</div>
            </div>
            <div className="visit-card-cell visit-card-cell-wide">
              <div className="visit-card-label">Vehicle Color</div>
              <div className="visit-card-value">{v.vehicleColor || '—'}</div>
            </div>
          </div>
          {v.pickupNotes && (
            <div className="visit-card-notes">
              <div className="visit-card-label">Pickup Notes</div>
              <div className="visit-card-value pickup-notes-value">{v.pickupNotes}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="page-container guest-pickup-report">
      <div className="admin-header-nav no-print">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="admin-title">Guest Pickup Report</h1>
        <div className="header-spacer" />
      </div>

      <div className="print-only print-header">
        <h1>Guest Pickup Report</h1>
        {reportData && (
          <div className="print-meta">
            <span>{TIMEFRAME_LABELS[reportData.timeframe] || reportData.timeframe}</span>
            <span> · </span>
            <span>{reportData.startDate.toLocaleDateString()} – {reportData.endDate.toLocaleDateString()}</span>
            <span> · </span>
            <span>Generated {new Date().toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="report-section no-print">
        <h2>Report Filters</h2>
        <div className="report-controls">
          <div className="control-group">
            <label htmlFor="gpr-timeframe">Timeframe</label>
            <select
              id="gpr-timeframe"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              disabled={loading}
            >
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="quarter">Past Quarter (3 Months)</option>
              <option value="semi-annual">Past 6 Months</option>
              <option value="annual">Past Year (12 Months)</option>
            </select>
          </div>
          <div className="control-group">
            <label htmlFor="gpr-search">Search Guest</label>
            <div className="search-wrapper">
              <input
                id="gpr-search"
                type="text"
                placeholder="Click to choose, or type to filter..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchDropdown(true);
                }}
                onClick={() => setShowSearchDropdown(true)}
                onFocus={() => setShowSearchDropdown(true)}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 150)}
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
              {showSearchDropdown && reportData && reportData.guests.length > 0 && (
                <div className="search-dropdown" role="listbox">
                  {filteredGuests.length === 0 ? (
                    <div className="search-dropdown-empty">No guests match.</div>
                  ) : (
                    filteredGuests.map((g) => (
                      <button
                        type="button"
                        key={g.name}
                        className="search-dropdown-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelectSearchGuest(g)}
                        role="option"
                      >
                        <span className="search-dropdown-avatar">
                          <img
                            src={g.image || placeholderAvatar}
                            alt={g.name}
                            onError={(e) => { e.currentTarget.src = placeholderAvatar; }}
                          />
                        </span>
                        <span className="search-dropdown-name">{g.name}</span>
                        <span className="search-dropdown-meta">
                          {g.visits.length} visit{g.visits.length === 1 ? '' : 's'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="control-actions">
            <button className="btn btn-outline" onClick={exportToCSV} disabled={loading || !reportData}>
              Export CSV
            </button>
            <button className="btn btn-primary" onClick={handlePrint} disabled={loading || !reportData}>
              Print
            </button>
          </div>
        </div>
      </div>

      {error && <div className="message message-error">{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div><p>Generating report...</p></div>
      ) : reportData ? (
        <>
          <div className="report-section">
            <div className="section-header">
              <h2>Summary</h2>
              <span className="section-period">
                {TIMEFRAME_LABELS[reportData.timeframe] || reportData.timeframe}
                {' '}
                <span className="section-dates">
                  ({reportData.startDate.toLocaleDateString()} – {reportData.endDate.toLocaleDateString()})
                </span>
              </span>
            </div>
            <div className="attendance-summary-grid pickup-summary-grid">
              <div className="attendance-summary-card guests">
                <div className="attendance-summary-number">{reportData.totalGuests}</div>
                <div className="attendance-summary-label">Guests with Pickup Data</div>
                <div className="attendance-summary-detail">unique guests in period</div>
              </div>
              <div className="attendance-summary-card sunday">
                <div className="attendance-summary-number">{reportData.visitsWithPickupData}</div>
                <div className="attendance-summary-label">Visits with Pickup Data</div>
                <div className="attendance-summary-detail">at least one pickup field set</div>
              </div>
              <div className="attendance-summary-card total">
                <div className="attendance-summary-number">{reportData.totalVisits}</div>
                <div className="attendance-summary-label">Total Visits in Period</div>
                <div className="attendance-summary-detail">all check-ins, regardless of pickup data</div>
              </div>
            </div>
          </div>

          <div className="report-section">
            <div className="section-header">
              <h2>Guest Details</h2>
              <span className="section-count">
                {filteredGuests.length} guest{filteredGuests.length === 1 ? '' : 's'}
              </span>
            </div>

            {filteredGuests.length === 0 ? (
              <div className="empty-state">
                {reportData.totalGuests === 0
                  ? 'No pickup data recorded in this period.'
                  : 'No guests match your search.'}
              </div>
            ) : (
              <div className="guest-list">
                {filteredGuests.map((guest) => (
                  <div key={guest.name} className="guest-block">
                    <div className="guest-header">
                      <div className="guest-header-left">
                        <div className="guest-avatar">
                          <img
                            src={guest.image || placeholderAvatar}
                            alt={guest.name}
                            onError={(e) => { e.currentTarget.src = placeholderAvatar; }}
                          />
                        </div>
                        <h3>{guest.name}</h3>
                      </div>
                      <span className="visit-count">
                        {guest.visits.length} visit{guest.visits.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {isMobile ? renderVisitCards(guest) : renderVisitTable(guest)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default GuestPickupReport;
